'use client';

import { type Ref, useEffect, useMemo, useState } from 'react';
import type { ButtonState } from './shared';

const STATE_CHANGE_DELAY_MS = 1000;

export function useButtonState<T extends HTMLElement = HTMLButtonElement>(
  isLoading: boolean,
  error?: any,
  onTransitionEnd?: VoidFunction,
): [ButtonState, Ref<T>] {
  const [buttonRef, setButtonRef] = useState<T | null>(null);
  const [state, setState] = useState<ButtonState>('initial');

  useEffect(() => {
    if (onTransitionEnd && buttonRef) {
      const transitionEndHandler = onTransitionEnd;

      function handleTransitionEnd(e: TransitionEvent) {
        if (e.target === buttonRef && e.propertyName === 'opacity' && getComputedStyle(buttonRef as Element)?.opacity === '1') {
          transitionEndHandler();
        }
      }

      buttonRef.addEventListener('transitionend', handleTransitionEnd);

      return () => {
        buttonRef.removeEventListener('transitionend', handleTransitionEnd);
      };
    }

    return () => {};
  }, [buttonRef, onTransitionEnd]);

  // const transitionPromise: Promise<boolean> = useMemo(
  //   () =>
  //     new Promise((resolve) => {
  //       if (buttonRef && ['success', 'error'].includes(state)) {
  //         function handleTransitionEnd(e: TransitionEvent) {
  //           if (e.target === buttonRef && e.propertyName === 'opacity' && getComputedStyle(buttonRef as Element)?.opacity === '1') {
  //             buttonRef?.removeEventListener('transitionend', handleTransitionEnd);
  //             resolve(true);
  //           }
  //         }
  //
  //         buttonRef.addEventListener('transitionend', handleTransitionEnd);
  //       } else {
  //         resolve(true);
  //       }
  //     }),
  //   [state, buttonRef],
  // );

  useEffect(() => {
    if (isLoading) {
      setState('loading');
      return () => {};
    }

    if (state !== 'initial') {
      setState(error ? 'error' : 'success');
      const timeoutHandle = window.setTimeout(() => {
        setState('initial');
      }, STATE_CHANGE_DELAY_MS);

      return () => {
        window.clearTimeout(timeoutHandle);
      };
    }

    return () => {};
  }, [isLoading, error, state]);

  return useMemo(() => [state, setButtonRef] as const, [state]);
}
